import { useRef } from "react";

const colors = { AVAILABLE: "#dcfce7", RESERVED: "#fef3c7", SEATED: "#fee2e2", BLOCKED: "#e5e7eb", INACTIVE: "#d1d5db" };
const structuralStyles = {
  WALL: { background: "#475569", borderColor: "#334155", color: "#ffffff" },
  WINDOW: { background: "#e0f2fe", borderColor: "#38bdf8", color: "#0369a1" },
  DOOR: { background: "#fef3c7", borderColor: "#b45309", color: "#92400e" },
  BAR: { background: "#f3e8ff", borderColor: "#a855f7", color: "#6b21a8" },
  RESTROOM: { background: "#e0e7ff", borderColor: "#818cf8", color: "#3730a3" },
};
const typeLabels = { TABLE: "میز", WALL: "دیوار", WINDOW: "پنجره", DOOR: "درب", BAR: "بار", CASHIER: "صندوق", STAGE: "سن", RESTROOM: "سرویس بهداشتی", DECORATION: "دکور" };
const stateLabels = { AVAILABLE: "آزاد", RESERVED: "رزرو شده", SEATED: "اشغال", BLOCKED: "مسدود", INACTIVE: "خارج از سرویس" };

export default function FloorPlanCanvas({ plan, elements, tables = [], states = {}, tableDetails = {}, selectedId, onSelect, onMove, editable = false, zoom = 1, large = false, fit = false, showGrid = true }) {
  const tableById = Object.fromEntries(tables.map((table) => [String(table._id || table.id), table]));
  const interactive = editable || Boolean(onSelect);
  const dragState = useRef(null);
  const pointerPosition = (event) => {
    const canvas = event.currentTarget.parentElement.getBoundingClientRect();
    return {
      x: (event.clientX - canvas.left) * (fit ? plan.width / canvas.width : 1 / zoom),
      y: (event.clientY - canvas.top) * (fit ? plan.height / canvas.height : 1 / zoom),
    };
  };
  const pointerDown = (event, element) => {
    if (!editable || !onMove) return;
    event.preventDefault();
    const pointer = pointerPosition(event);
    dragState.current = { id: element._id, offsetX: pointer.x - element.x, offsetY: pointer.y - element.y, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
    onSelect?.(element._id);
  };
  const pointerMove = (event) => {
    if (!dragState.current || !editable || !onMove) return;
    const pointer = pointerPosition(event);
    dragState.current.moved = true;
    onMove(dragState.current.id, pointer.x - dragState.current.offsetX, pointer.y - dragState.current.offsetY);
  };
  const pointerUp = (event) => {
    if (!dragState.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    dragState.current = null;
  };
  return <div className={`${fit ? "overflow-hidden" : "overflow-auto"} rounded-2xl border border-gray-200 bg-gray-100 p-3 ${large && !fit ? "min-h-[720px]" : ""}`}>
    <div className="relative origin-top-right bg-white shadow-inner" style={fit ? { width: "100%", aspectRatio: `${plan.width} / ${plan.height}` } : { width: plan.width, height: plan.height, transform: `scale(${zoom})`, marginBottom: plan.height * (zoom - 1) }}>
      {showGrid && <div className="pointer-events-none absolute inset-0 opacity-20" style={{ backgroundImage: "linear-gradient(#94a3b8 1px,transparent 1px),linear-gradient(90deg,#94a3b8 1px,transparent 1px)", backgroundSize: "20px 20px" }} />}
      {elements.map((element) => {
        const table = tableById[String(element.tableId)];
        const state = states[String(element.tableId)] || (table?.isActive === false ? "INACTIVE" : "AVAILABLE");
        const isTable = element.type === "TABLE";
        const selected = String(selectedId) === String(element._id);
        const Element = interactive ? "button" : "div";
        const statePattern = ["BLOCKED", "INACTIVE"].includes(state)
          ? { backgroundImage: "repeating-linear-gradient(135deg, transparent 0, transparent 6px, rgba(71,85,105,.12) 6px, rgba(71,85,105,.12) 10px)" }
          : {};
        return <Element
          type={interactive ? "button" : undefined}
          role={!interactive && isTable ? "img" : undefined}
          aria-label={isTable ? `${table?.name || "میز"}، ${stateLabels[state] || state}` : undefined}
          key={element._id}
          onPointerDown={interactive ? (event) => pointerDown(event, element) : undefined}
          onPointerMove={interactive ? pointerMove : undefined}
          onPointerUp={interactive ? pointerUp : undefined}
          onPointerCancel={interactive ? pointerUp : undefined}
          onClick={interactive ? () => onSelect?.(element._id) : undefined}
          title={editable ? "برای انتخاب کلیک کنید؛ برای جابه‌جایی بکشید" : undefined}
          className={`absolute flex items-center justify-center border-2 px-1 text-center text-xs font-bold transition-shadow ${selected ? "border-blue-600 ring-2 ring-blue-200" : "border-gray-400"} ${isTable && table?.shape === "ROUND" ? "rounded-full" : "rounded-md"}`}
          style={{ left: fit ? `${element.x / plan.width * 100}%` : element.x, top: fit ? `${element.y / plan.height * 100}%` : element.y, width: fit ? `${element.width / plan.width * 100}%` : element.width, height: fit ? `${element.height / plan.height * 100}%` : element.height, cursor: editable ? "move" : interactive ? "pointer" : "default", touchAction: editable ? "none" : "auto", userSelect: "none", transform: `rotate(${element.rotation || 0}deg)`, zIndex: element.zIndex || 0, ...(isTable ? { background: colors[state], ...statePattern } : (structuralStyles[element.type] || { background: "#f8fafc" })) }}
        >
          {isTable ? <span>{table?.name || "میز"}<small className="mt-1 block font-normal">{tableDetails[String(element.tableId)] || stateLabels[state] || state}</small></span> : typeLabels[element.type] || element.type}
        </Element>;
      })}
    </div>
  </div>;
}
